import { Box, Stack } from '@mui/material'
import { alpha } from '@mui/material/styles'

import failureAtlasDiagram from 'assets/images/foresight-failure-atlas.svg'
import {
  PAGE_PADDING_Y,
  PROMO_MAX_HEIGHT,
  PROMO_MIN_HEIGHT,
  PROMO_WIDTH,
  SIDE_BY_SIDE
} from 'components/authentication/foresight-promo/foresight-promo-utils'
import Image from 'components/common/Image'
import { MUITrackedExternalLink } from 'components/common/MUITrackedExternalLink'
import { GREMLIN_COM_DOCS_LINKS } from 'constants/external-urls-gremlin-docs'
import { muiTheme } from 'mui-theme'
import { getBrandGradient } from 'mui-theme/gradient-utils'
import { colors } from 'mui-theme/theme'

const CARD_BACKGROUND_OPACITY = 0.18
const DESCRIPTION_MAX_WIDTH = 500
const DIAGRAM_HEIGHT = 354
const DIAGRAM_MIN_HEIGHT = 200
const DIAGRAM_WIDTH = 344
const FONT_FAMILY = "'Merriweather Sans', sans-serif"

export const FORESIGHT_PROMO_LABEL = 'Introducing Foresight AI'

const learnMoreGradientEnd = muiTheme.palette.marketingBlue400.main
const learnMoreGradientStart = muiTheme.palette.orchid.dark

export function ForesightPromo() {
  return (
    <Stack
      aria-label={FORESIGHT_PROMO_LABEL}
      component="aside"
      sx={{
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        width: '100%',
        padding: '40px 32px',
        [SIDE_BY_SIDE]: {
          width: PROMO_WIDTH,
          height: `min(${PROMO_MAX_HEIGHT}px, calc(100vh - 2 * ${PAGE_PADDING_Y}))`,
          minHeight: PROMO_MIN_HEIGHT,
          padding: 6
        },
        borderRadius: 4,
        overflow: 'hidden',
        fontFamily: FONT_FAMILY,
        color: 'common.white',
        textAlign: 'center',
        backgroundColor: 'common.black',
        backgroundImage: getBrandGradient(-31, CARD_BACKGROUND_OPACITY),
        boxShadow: `0 20px 40px -15px ${alpha(colors.primary[900], 0.5)}`
      }}
    >
      <Stack sx={{ alignItems: 'center', gap: 3 }}>
        <Stack sx={{ alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              position: 'relative',
              padding: '5px 15px',
              borderRadius: '30px',
              fontSize: 13,
              fontWeight: 300,
              lineHeight: 1.2,
              letterSpacing: '1.04px',
              textTransform: 'uppercase',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                padding: '1.5px',
                borderRadius: 'inherit',
                background: getBrandGradient(-90),
                mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
                maskComposite: 'exclude',
                pointerEvents: 'none'
              }
            }}
          >
            Agentic resilience
          </Box>
          <Stack
            component="h2"
            sx={{
              gap: 0.5,
              fontWeight: 700,
              color: 'inherit',
              fontSize: 40,
              [SIDE_BY_SIDE]: { fontSize: 60 }
            }}
          >
            <Box
              component="span"
              sx={{ lineHeight: 1.1, letterSpacing: '-0.015em' }}
            >
              Introducing
            </Box>
            <Box
              component="span"
              sx={{
                lineHeight: 1.05,
                letterSpacing: '-0.02em',
                backgroundImage: getBrandGradient(-33),
                backgroundClip: 'text',
                color: 'transparent'
              }}
            >
              Foresight AI
            </Box>
          </Stack>
        </Stack>
        {/* `p` over Typography since its variant styles would be overridden by the promo's type scale */}
        <Box
          component="p"
          sx={{
            maxWidth: DESCRIPTION_MAX_WIDTH,
            fontSize: 16,
            [SIDE_BY_SIDE]: { fontSize: 20 },
            lineHeight: '24px',
            color: 'grey.50',
            textWrap: 'balance'
          }}
        >
          Foresight AI analyzes and tests your systems for potential failures,
          fixes them, and verifies resilience at the speed of AI.
        </Box>
        <MUITrackedExternalLink
          eventName="ForesightPromoLearnMoreClicked"
          href={GREMLIN_COM_DOCS_LINKS.FORESIGHT_AI_ANNOUNCEMENT_BLOG}
          sx={{
            padding: '10px 24px',
            border: `1px solid ${learnMoreGradientStart}`,
            borderRadius: 3,
            backgroundImage: `linear-gradient(129.94deg, ${learnMoreGradientStart} 0%, ${learnMoreGradientEnd} 100%)`,
            fontSize: 15,
            lineHeight: 1.5,
            textTransform: 'uppercase',
            color: 'inherit',
            '&:hover': { filter: 'brightness(1.1)' },
            '&:focus-visible': {
              outline: '2px solid white',
              outlineOffset: '2px'
            }
          }}
          underline="none"
        >
          Learn more
        </MUITrackedExternalLink>
      </Stack>
      <Box
        alt="Failure Atlas cycle: find it, fix it, prove it"
        component={Image}
        height={DIAGRAM_HEIGHT}
        src={failureAtlasDiagram}
        sx={{
          display: 'none',
          [SIDE_BY_SIDE]: {
            display: 'block',
            flex: '0 1 auto',
            minHeight: DIAGRAM_MIN_HEIGHT,
            width: 'auto',
            objectFit: 'contain'
          }
        }}
        width={DIAGRAM_WIDTH}
      />
    </Stack>
  )
}
